import React from 'react';
import {
  VictoryChart,
  VictoryLine,
  VictoryAxis,
  VictoryTheme,
  VictoryTooltip,
  VictoryVoronoiContainer,
  VictoryLegend,
  VictoryScatter,
  VictoryLabel,
} from 'victory';
import { formatPeriodES } from '../../../config/reportTranslations';
import './Charts.css';
import { REPORT_CHART_COLORS } from '../../../constants/colors';

const COLORS = REPORT_CHART_COLORS;

/**
 * Gráfico 05: Intereses Causados/Activo Productivo
 *
 * Line chart showing cost/productive assets ratio (funding cost) over time.
 * Displays two lines:
 * - COAC value (institution-specific)
 * - S1 benchmark (segment average)
 *
 * Matches PDF report format from Atuntaqui_mar2025
 */

export interface Grafico05Data {
  period: string;                   // YYYY-MM format
  costo_activo_productivo: number;  // Institution cost ratio (e.g., 0.0669 = 6.69%)
  benchmark_s1?: number;            // Segment 1 benchmark (optional)
}

export interface Grafico05Props {
  data: Grafico05Data[];
  title?: string;
}

export const Grafico05: React.FC<Grafico05Props> = ({
  data,
  title = 'Gráfico 05: Intereses Causados/Activo Productivo'
}) => {
  if (!data || data.length === 0) {
    return (
      <div className="chart-container">
        <h4 className="chart-title">{title}</h4>
        <p className="chart-no-data">No hay datos disponibles para este período</p>
      </div>
    );
  }

  // Sort data by period (oldest first for line chart)
  const sortedData = [...data].sort((a, b) => a.period.localeCompare(b.period));

  // Transform data for Victory - convert to percentage
  // Line data has NO labels to avoid duplicates
  const coacLineData = sortedData.map(d => ({
    x: formatPeriodES(d.period),
    y: d.costo_activo_productivo * 100,
  }));

  // Scatter data has labels for display
  const coacScatterData = sortedData.map(d => ({
    x: formatPeriodES(d.period),
    y: d.costo_activo_productivo * 100,
    label: `${(d.costo_activo_productivo * 100).toFixed(1)}%`
  }));

  // Check if we have benchmark data
  const hasBenchmark = sortedData.some(d => d.benchmark_s1 !== undefined);
  const benchmarkLineData = hasBenchmark
    ? sortedData.map(d => ({
        x: formatPeriodES(d.period),
        y: (d.benchmark_s1 || 0) * 100,
      }))
    : [];
  const benchmarkScatterData = hasBenchmark
    ? sortedData.map(d => ({
        x: formatPeriodES(d.period),
        y: (d.benchmark_s1 || 0) * 100,
        label: `${((d.benchmark_s1 || 0) * 100).toFixed(1)}%`
      }))
    : [];

  // Calculate y-axis domain with some padding
  const allValues = [
    ...coacLineData.map(d => d.y),
    ...benchmarkLineData.map(d => d.y)
  ];
  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);
  const padding = (maxValue - minValue) * 0.15 || 1;
  const yDomain: [number, number] = [
    Math.max(0, minValue - padding),
    maxValue + padding
  ];

  return (
    <div className="chart-container">
      <h4 className="chart-title">{title}</h4>

      <div className="chart-wrapper">
        <VictoryChart
          theme={VictoryTheme.material}
          domainPadding={{ x: 20, y: 10 }}
          padding={{ top: 40, bottom: 60, left: 55, right: 20 }}
          height={280}
          containerComponent={
            <VictoryVoronoiContainer
              labels={({ datum }) => datum.label}
              labelComponent={<VictoryTooltip style={{ fontSize: 9 }} />}
            />
          }
        >
          {/* Legend */}
          <VictoryLegend
            x={60}
            y={5}
            orientation="horizontal"
            gutter={20}
            style={{
              labels: { fontSize: 9 }
            }}
            data={[
              { name: 'Costo/Activo productivo promedio (COAC)', symbol: { fill: COLORS.secondary } },
              ...(hasBenchmark ? [{ name: 'Costo/Activo productivo promedio (S1)', symbol: { fill: COLORS.primary } }] : [])
            ]}
          />

          {/* X-Axis */}
          <VictoryAxis
            style={{
              axis: { stroke: '#666' },
              tickLabels: {
                fontSize: 9,
                padding: 5,
                angle: -45,
                textAnchor: 'end'
              },
              grid: { stroke: 'none' }
            }}
          />

          {/* Y-Axis */}
          <VictoryAxis
            dependentAxis
            domain={yDomain}
            tickFormat={(t: number) => `${t.toFixed(0)}%`}
            style={{
              axis: { stroke: '#666' },
              tickLabels: { fontSize: 9, padding: 5 },
              grid: { stroke: '#e0e0e0', strokeDasharray: '3,3' }
            }}
          />

          {/* COAC Line (teal) - NO labels on line */}
          <VictoryLine
            data={coacLineData}
            style={{
              data: {
                stroke: COLORS.secondary,
                strokeWidth: 1.2
              }
            }}
          />

          {/* COAC Points with small value labels */}
          <VictoryScatter
            data={coacScatterData}
            size={3}
            style={{ data: { fill: COLORS.secondary } }}
            labelComponent={<VictoryLabel dy={-8} style={{ fontSize: 8, fill: COLORS.secondary }} />}
          />

          {/* Benchmark Line (S1) - if available - dark gray - NO labels on line */}
          {hasBenchmark && (
            <>
              <VictoryLine
                data={benchmarkLineData}
                style={{
                  data: {
                    stroke: COLORS.primary,
                    strokeWidth: 1.2,
                    strokeDasharray: '5,5'
                  }
                }}
              />
              <VictoryScatter
                data={benchmarkScatterData}
                size={3}
                style={{ data: { fill: COLORS.primary } }}
                labelComponent={<VictoryLabel dy={-8} style={{ fontSize: 8, fill: COLORS.primary }} />}
              />
            </>
          )}
        </VictoryChart>
      </div>

      <p className="chart-source">
        Fuente: COAC / Elaboración: UnionRatings
      </p>
    </div>
  );
};

export default Grafico05;
